import { useCart } from '../context/CartContext';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingCart, ArrowRight, ShieldCheck, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const COIN_COLORS = { BTC:'#F7931A', ETH:'#627EEA', BNB:'#F0B90B', SOL:'#9945FF', USDT:'#26A17B', SUI:'#4DA2FF', ASTER:'#FF6B00', HYPE:'#E040FB', XRP:'#0052FF' };
const CURRENCIES = ['BTC','ETH','BNB','SOL','USDT','SUI','ASTER','HYPE','XRP'];

export default function Cart() {
  const { cart, sellerGroups, paymentCount, removeFromCart, updateQuantity, updateItemCurrency, clearCart, cartCount, cartTotalUSD } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!user) { navigate('/login'); return; }
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-20 pb-24 flex flex-col items-center justify-center px-4">
        <ShoppingCart size={64} className="text-brand-muted mb-4" />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Sora' }}>Your cart is empty</h2>
        <p className="text-brand-muted mb-6">Add some products to get started</p>
        <Link to="/" className="btn-primary px-8">Browse Products</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-36 px-4 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mt-4 mb-4">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Sora' }}>
          Cart <span className="text-brand-muted text-lg">({cartCount})</span>
        </h1>
        <button onClick={clearCart} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
          <Trash2 size={13} /> Clear all
        </button>
      </div>

      {/* Multi-payment notice */}
      {paymentCount > 1 && (
        <div className="p-3 rounded-xl mb-4 text-sm" style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)' }}>
          <p className="font-semibold text-yellow-300 mb-1">⚡ {paymentCount} Separate Payments Required</p>
          <p className="text-yellow-200 text-xs">
            Your cart has items from {paymentCount} different sellers. Each seller receives their own separate crypto payment. You can choose which coin to pay each seller with below.
          </p>
        </div>
      )}

      {/* Group items by seller */}
      <div className="space-y-5 mb-6">
        {sellerGroups.map((group, gi) => {
          const groupTotal = group.items.reduce((s, i) => s + (i.priceUSD * 1.015 * i.quantity), 0);
          // Default payment currency = first item's listed currency
          const defaultCoin = group.items[0]?.selectedCurrency || group.items[0]?.listedCurrency || 'BNB';
          const coinColor = COIN_COLORS[defaultCoin] || '#FF6B00';

          return (
            <div key={group.sellerId} className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${coinColor}30` }}>
              {/* Seller header */}
              <div className="flex items-center justify-between px-4 py-2.5" style={{ background: `${coinColor}10`, borderBottom: `1px solid ${coinColor}20` }}>
                <div>
                  <span className="text-xs font-bold" style={{ color: coinColor }}>
                    🏪 {group.sellerUsername || 'Seller ' + (gi + 1)}
                  </span>
                  <span className="text-xs text-brand-muted ml-2">· Payment #{gi + 1}</span>
                </div>
                <span className="text-xs font-bold font-mono" style={{ color: coinColor }}>
                  ${groupTotal.toFixed(2)} USD
                </span>
              </div>

              {/* Items */}
              <div className="p-3 space-y-3" style={{ background: '#12121A' }}>
                {group.items.map((item, idx) => {
                  const color = COIN_COLORS[item.selectedCurrency] || '#FF6B00';
                  return (
                    <div key={idx} className="flex gap-3">
                      <Link to={`/product/${item.productId}`}>
                        <img src={item.image || 'https://via.placeholder.com/72/12121A/FF6B00?text=CM'}
                          alt={item.title} className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                          style={{ border: '1px solid #1E1E2E' }} />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/product/${item.productId}`}>
                          <p className="text-sm font-semibold line-clamp-2 hover:text-brand-orange transition-colors">{item.title}</p>
                        </Link>
                        {item.selectedVariant && <p className="text-xs text-brand-muted">{item.selectedVariant}</p>}
                        {/* Listed in */}
                        <p className="text-xs text-brand-muted mt-0.5">
                          Listed in: <span className="font-bold" style={{ color: COIN_COLORS[item.listedCurrency] }}>{item.listedCurrency}</span>
                        </p>
                        {/* Price */}
                        <p className="text-sm font-bold font-mono mt-0.5" style={{ color }}>
                          ≈ ${(item.priceUSD * 1.015 * item.quantity).toFixed(2)} USD
                        </p>
                        {/* Qty controls */}
                        <div className="flex items-center gap-2 mt-1.5">
                          <div className="flex items-center gap-1.5 rounded-lg p-0.5" style={{ background: '#0A0A0F', border: '1px solid #1E1E2E' }}>
                            <button onClick={() => updateQuantity(item.productId, item.quantity - 1, item.selectedVariant)}
                              className="w-6 h-6 rounded flex items-center justify-center text-brand-muted hover:text-white" style={{ background: '#1E1E2E' }}>
                              <Minus size={10} />
                            </button>
                            <span className="text-sm font-semibold w-5 text-center">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.productId, item.quantity + 1, item.selectedVariant)}
                              disabled={item.quantity >= item.stock}
                              className="w-6 h-6 rounded flex items-center justify-center text-brand-muted hover:text-white disabled:opacity-30" style={{ background: '#1E1E2E' }}>
                              <Plus size={10} />
                            </button>
                          </div>
                          <button onClick={() => removeFromCart(item.productId, item.selectedVariant)} className="text-red-400 hover:text-red-300 p-1">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pay this seller with — coin picker per seller group */}
                <div className="pt-2" style={{ borderTop: '1px solid #1E1E2E' }}>
                  <p className="text-xs text-brand-muted mb-2">
                    Pay <strong className="text-white">{group.sellerUsername || 'this seller'}</strong> with:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {CURRENCIES.map(c => {
                      // All items in this group use same selected currency
                      const isSelected = group.items[0]?.selectedCurrency === c;
                      return (
                        <button key={c}
                          onClick={() => group.items.forEach(item => updateItemCurrency(item.productId, c, item.selectedVariant))}
                          className="px-2.5 py-1 rounded-lg text-xs font-bold transition-all"
                          style={{
                            background: isSelected ? `${COIN_COLORS[c]}20` : '#0A0A0F',
                            color: isSelected ? COIN_COLORS[c] : '#6B6B8A',
                            border: `1px solid ${isSelected ? COIN_COLORS[c] : '#1E1E2E'}`,
                            fontFamily: 'JetBrains Mono',
                          }}>
                          {c}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-brand-muted mt-1.5">
                    💡 Price stays the same in USD — coin amount adjusts with live market rate
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total summary */}
      <div className="glass rounded-2xl p-4 mb-4">
        <div className="space-y-1.5 mb-3">
          <div className="flex justify-between text-sm">
            <span className="text-brand-muted">Subtotal</span>
            <span>${(cartTotalUSD / 1.015).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-brand-muted">Network fees (1.5%)</span>
            <span className="text-brand-orange">+${(cartTotalUSD - cartTotalUSD / 1.015).toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold text-base pt-2" style={{ borderTop: '1px solid #1E1E2E' }}>
            <span>Total</span>
            <span className="gradient-text">${cartTotalUSD.toFixed(2)} USD</span>
          </div>
        </div>

        {paymentCount > 1 && (
          <p className="text-xs text-brand-muted mb-3">
            = {paymentCount} separate crypto payments (one per seller)
          </p>
        )}

        <div className="flex items-center gap-2 text-xs text-brand-muted mb-4">
          <ShieldCheck size={13} className="text-green-400 flex-shrink-0" />
          <span>Each payment held in escrow — funds only release when you receive your order</span>
        </div>

        <button onClick={handleCheckout} className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base">
          Proceed to Checkout ({paymentCount} payment{paymentCount > 1 ? 's' : ''}) <ArrowRight size={16} />
        </button>
      </div>

      <Link to="/" className="block text-center text-sm text-brand-muted hover:text-white transition-colors">
        ← Continue Shopping
      </Link>
    </div>
  );
}
